import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  TUpdateLabelChatValidation,
  updateLabelChatValidationSchema,
  useUpdateLabelChatMutation,
} from '../../../api/update-label-chat.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CHAT_QUERY_KEY } from '../../../api/get-list-label-chat.api';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import { Spinner } from '@/components/_ui/spinner';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';

export type TUpdateLabelChatModalType = 'UpdateLabelChatModal';
export interface IUpdateLabelChatModalProps {
  labelChatId: string;
  name?: string;
  color?: string;
}
const UpdateLabelChatModal = memo(({ labelChatId, name, color }: IUpdateLabelChatModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const isOpenUpdateLabelChatModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('UpdateLabelChatModal');
  }, [caseModuleModalRootActions]);

  const showToast = useCustomToast();
  const form = useForm<TUpdateLabelChatValidation>({
    resolver: zodResolver(updateLabelChatValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelChatId: '',
      name: '',
      color: '',
    },
  });

  useEffect(() => {
    if (labelChatId || name || color) {
      form.reset({
        labelChatId: labelChatId || '',
        name: name || '',
        color: color || '',
      });
    }
  }, [labelChatId, name, color]);

  const mutationUpdateLabelChat = useUpdateLabelChatMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error update label chat, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CHAT_QUERY_KEY] });
        caseModuleModalRootActions.closeModal('UpdateLabelChatModal');
        form.reset();
        showToast({
          message: 'Label chat update successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitUpdateLabelChat = useCallback(
    (data: TUpdateLabelChatValidation) => {
      mutationUpdateLabelChat.mutate({
        labelChatId: data?.labelChatId || '',
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationUpdateLabelChat]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('UpdateLabelChatModal');
    form.reset();
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal isOpen={isOpenUpdateLabelChatModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Edit Label Chat</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitUpdateLabelChat)(e);
          }}
          id="edit-label-chat-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-chat" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-chat" placeholder="Enter Label Chat Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-chat" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-chat" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="edit-label-chat-form" type="submit" disabled={mutationUpdateLabelChat.isPending}>
          {mutationUpdateLabelChat.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
UpdateLabelChatModal.displayName = 'UpdateLabelChatModal';

export default UpdateLabelChatModal;
